import { cardinalPointFromDegrees, haversineDistanceMeters } from '@agavi/blocks';
import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmtLocale } from './number-format';
import {
  clampToLimit, fitViewport, grabTargetAt, gridLinesFor, PIN_GRAB_PX, scaleBarFor, screenToWorld,
  worldToScreen, type LatLng, type PlanViewport,
} from './sensor-plan-geometry';

import { IconButton } from '@/components/ui/icon-button';

/**
 * Where the phone is, and which way it is pointing — one object, because that is what a phone is.
 *
 * Drag the body to move it, drag the arrow to swivel it. Two grab targets on one pin, so either
 * channel can be tested without a mode switch, and neither can be set to something the other
 * contradicts.
 *
 * NO MAP TILES, deliberately for now. What a teacher needs to judge is geometry relative to the
 * target — am I inside the circle, how far outside — and that is drawn here exactly to scale with a
 * scale bar to read it against. Real tiles slot in later as a background layer under this one,
 * changing nothing above it; they are held back only because the public tile servers disallow
 * production use and a keyed provider is a product decision.
 */

/** Pixel radius of the heading ring around the pin. Fixed rather than scaled, so the arrow is the
 *  same grab target at every zoom — a ring in metres would vanish when zoomed out. */
const RING_PX = 34;
const PIN_PX = 9;

/**
 * The keyboard handle sitting over the pin or the arrowhead.
 *
 * These were `sr-only`, so tabbing in showed nothing at all and — with two of them — gave no way to
 * tell whether the arrow keys would move the pin or swing the heading. Drawing a ring inside the
 * SVG was the first fix and it used `ws-primary`, which is the one colour `globals.css` records as
 * having been TRIED AND REJECTED for a focus ring: that aquamarine is the same hue as half the
 * chrome around it, so the thing that must stand out blended in.
 *
 * So the handle is a real element at the right place instead, carrying the app's single
 * `outline:2px_auto` — the browser's own bright blue, the same ring as every other control here.
 * `pointer-events-none` keeps the drag on the SVG underneath; focus is unaffected by it.
 */
const SLIDER_HANDLE_CLASS = 'pointer-events-none absolute h-[30px] w-[30px] rounded-full focus-visible:[outline:2px_auto]';

export interface PlanTarget {
  id: string;
  /** The block's own name, so a chip can say "At Site A" rather than a coordinate. */
  name: string;
  at: LatLng;
  radiusMeters: number;
}

export interface HeadingBand {
  id: string;
  name: string;
  /** Degrees, clockwise from north. `from` may exceed `to` where the band crosses the seam. */
  from: number;
  to: number;
}

type Grab = 'pin' | 'arrow' | null;

export function SensorPlanView({
  targets, bands, position, heading, onPositionChange, onHeadingChange, onReset, showPin, showRing, disabled, fillHeight, compact,
}: {
  targets: PlanTarget[];
  bands: HeadingBand[];
  position: LatLng;
  heading: number;
  onPositionChange: (next: LatLng) => void;
  onHeadingChange: (next: number) => void;
  /** False when no block reads position. The pin still DRAWS — a true-north compass needs a
   *  position for its declination — but nothing invites dragging it. */
  /** Puts the device back where it started: the first target, pointing north. */
  onReset: () => void;
  showPin: boolean;
  showRing: boolean;
  /**
   * Take the host's HEIGHT rather than deriving one from the width.
   *
   * Phone landscape is why. The canvas is normally 0.72 of its width, which on an 844x390 screen
   * asks for a 590px-tall map on a 390px-tall viewport — so it overflowed and the controls below
   * it scrolled off. In a landscape row the parent hands this a definite height and the map fills
   * it, coming out wide and short, which is the shape of the screen it is on.
   */
  fillHeight?: boolean;
  /**
   * Shorter, because it is not the only channel in the panel.
   *
   * At 0.72 of its width the map is about 260px, and stacked with the angle dial the two ran well
   * past the panel's own maximum — so the dial sat below the fold and a teacher had to scroll a
   * panel to find the control they had opened it for. A teacher testing an angle does not need a
   * map that size; one that still shows the pin and its tolerance is enough.
   */
  compact?: boolean;
  /**
   * The real sensor is being read, so nothing simulated applies.
   *
   * Dimmed and inert rather than removed: a teacher who has just switched to Device needs to see
   * that the map is still THERE and why it stopped responding. Taking it away would read as a bug,
   * and switching back would make the panel jump.
   */
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const grabRef = useRef<Grab>(null);

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const measure = () => setSize({
      width: node.clientWidth,
      // `fillHeight` reads the host's own box; otherwise the height is derived from the width, which
      // a host with `height: auto` cannot supply without asking the SVG it contains.
      height: fillHeight && node.clientHeight > 0
        ? node.clientHeight
        : Math.round(node.clientWidth * (compact ? 0.42 : 0.72)),
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [fillHeight, compact]);

  const view: PlanViewport = fitViewport(targets, position, size.width, size.height);
  const pin = worldToScreen(position, view);
  const bar = scaleBarFor(view);
  /**
   * Anchored to the first target, so grid lines pass through the thing being measured against and
   * distance can be counted in squares from it. Falls back to the pin when the task has no target
   * yet, where any anchor is as arbitrary as any other.
   */
  const grid = gridLinesFor(view, targets[0]?.at ?? position);

  /** Where the arrow tip sits, in screen pixels. Bearing 0 is north, which is up. */
  const tipAt = (deg: number) => ({
    x: pin.x + Math.sin((deg * Math.PI) / 180) * RING_PX,
    y: pin.y - Math.cos((deg * Math.PI) / 180) * RING_PX,
  });
  const tip = tipAt(heading);
  /**
   * Where the bearing text sits: further out along the same line as the arrow, and ANCHORED by the
   * direction it points.
   *
   * A centred label overlapped the arrowhead at due east and west, and no amount of extra distance
   * fixes that reliably — "NE 45°" is wider than "N 0°", so the overlap depends on the text. What
   * does fix it is anchoring: pointing right, the text starts to the right of the tip; pointing
   * left, it ends to the left of it. Only a near-vertical bearing stays centred, which is the one
   * case where the label sits above or below the arrow rather than beside it.
   */
  const LABEL_GAP = RING_PX + 16;
  const labelAnchor: 'start' | 'middle' | 'end' = heading > 20 && heading < 160 ? 'start'
    : heading > 200 && heading < 340 ? 'end'
    : 'middle';
  const label = {
    x: pin.x + Math.sin((heading * Math.PI) / 180) * LABEL_GAP
      + (labelAnchor === 'start' ? 6 : labelAnchor === 'end' ? -6 : 0),
    y: pin.y - Math.cos((heading * Math.PI) / 180) * LABEL_GAP
      // Nudged down when centred so a due-north label clears the arrowhead, and up when beside it
      // so the text sits on the arrow's own line rather than under it.
      + (labelAnchor === 'middle' ? (heading > 90 && heading < 270 ? 14 : -6) : 4),
  };

  const pointAt = useCallback((e: React.PointerEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    return rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : { x: 0, y: 0 };
  }, []);

  function handlePointerDown(e: React.PointerEvent) {
    if (disabled) return;
    // Stops the browser starting a text selection or an image drag from this press, which it does
    // before any of our own handling gets a say.
    e.preventDefault();
    const p = pointAt(e);
    grabRef.current = grabTargetAt(p, { pin, tip, showPin, showRing });
    if (!grabRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    apply(p);
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!grabRef.current) return;
    apply(pointAt(e));
  }

  function apply(p: { x: number; y: number }) {
    if (grabRef.current === 'pin') {
      onPositionChange(clampToLimit(screenToWorld(p, view), targets));
    } else if (grabRef.current === 'arrow') {
      // atan2 with the arguments in this order, and y negated, gives a compass bearing: clockwise
      // from north, rather than the anticlockwise-from-east that atan2 returns by default.
      const deg = (Math.atan2(p.x - pin.x, pin.y - p.y) * 180) / Math.PI;
      onHeadingChange((deg + 360) % 360);
    }
  }

  const endGrab = () => { grabRef.current = null; };

  /** The nearest target and how far the pin is from it — the fact a listener needs, and the one a
   *  sighted teacher reads off the circle. */
  const nearest = targets.length
    ? targets
      .map(tg => ({ tg, d: haversineDistanceMeters(tg.at, position) }))
      .sort((a, b) => a.d - b.d)[0]!
    : null;

  const distanceText = nearest
    ? t(nearest.d <= nearest.tg.radiusMeters
      ? 'task.sensorSim.positionInside'
      : 'task.sensorSim.positionOutside', {
      meters: fmtLocale(Math.round(nearest.d), i18n.language),
      target: nearest.tg.name,
    })
    : t('task.sensorSim.positionNoTarget');

  const inBand = bands.find(b => (b.from <= b.to
    ? heading >= b.from && heading <= b.to
    : heading >= b.from || heading <= b.to));
  const headingText = t(inBand ? 'task.sensorSim.headingInside' : 'task.sensorSim.headingOutside', {
    degrees: Math.round(heading),
    target: inBand?.name ?? '',
  });

  /** One arc of the heading ring, as an SVG path. Drawn on a slightly larger radius than the arrow
   *  so the two never sit on top of each other. */
  function bandPath(b: HeadingBand): string {
    const r = RING_PX + 6;
    const a = tipAt(b.from), z = tipAt(b.to);
    const from = { x: pin.x + (a.x - pin.x) * (r / RING_PX), y: pin.y + (a.y - pin.y) * (r / RING_PX) };
    const to = { x: pin.x + (z.x - pin.x) * (r / RING_PX), y: pin.y + (z.y - pin.y) * (r / RING_PX) };
    const sweep = (b.to - b.from + 360) % 360;
    return `M ${from.x} ${from.y} A ${r} ${r} 0 ${sweep > 180 ? 1 : 0} 1 ${to.x} ${to.y}`;
  }

  return (
    <div ref={hostRef} className={`relative w-full ${fillHeight ? 'min-h-0 flex-1' : ''}`}>
      {/*
        The drawing is `aria-hidden` and the two sliders below carry the whole control.

        Same split the sonification player uses: pointer work on the visual, keyboard and assistive
        technology on an sr-only control that reports the DERIVED fact. "120 metres from Site A,
        outside tolerance" is what a sighted teacher reads off the circle; a raw coordinate pair is
        what neither of them wants.
      */}
      <svg
        ref={svgRef}
        aria-hidden='true'
        width={size.width}
        height={size.height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endGrab}
        onPointerCancel={endGrab}
        /* `select-none`, and every label below is `pointer-events-none`.
           A drag across the canvas was selecting the labels it passed over — the target names, the
           distances, the coordinates — so the pin came with a blue smear and the next drag started
           a selection instead of a move. Two halves to the fix: nothing here is selectable, and a
           pointerdown that lands ON a label falls through to the canvas rather than being caught
           by the text. */
        /* NO CURSOR ON THE CANVAS. It carried `cursor-grab` across its whole area, which was
           true when any press moved the pin and became a lie the moment only the handles did.
           The grab cursor lives on the pin and the arrowhead instead, so what the pointer says
           and what a press does are the same statement. */
        className={`select-none touch-none rounded-lg border border-ws-border bg-ws-btn transition-opacity ${
          disabled ? 'opacity-40' : ''}`}
      >
        {/*
          The grid, under everything, because it is the ground rather than a mark on it.

          With no tiles there is nothing on screen that says you are moving: a pin travelling across
          a blank field looks like a pin standing still. These lines are fixed to the WORLD, so they
          slide under the pin as it moves and hold their place as the fit changes — and one square
          is exactly the scale bar's length, so the same label reads both.
        */}
        <g aria-hidden='true'>
          {grid.xs.map(x => (
            <line key={`vx${x}`} x1={x} y1={0} x2={x} y2={size.height} className='stroke-ws-border/60' strokeWidth={1} />
          ))}
          {grid.ys.map(y => (
            <line key={`hy${y}`} x1={0} y1={y} x2={size.width} y2={y} className='stroke-ws-border/60' strokeWidth={1} />
          ))}
        </g>

        {/*
          A line from every target to the pin, labelled with the distance.
          
          One per target rather than only the nearest, because that is the fact the consolidation
          exists to show: one device, several blocks, and a position that satisfies one target is a
          measured distance from all the others. It also retires the sentence that used to sit under
          the canvas — the number belongs on the thing it measures, not in a caption below it.
        */}
        {targets.map(tg => {
          const c = worldToScreen(tg.at, view);
          const d = haversineDistanceMeters(tg.at, position);
          const inside = d <= tg.radiusMeters;
          return (
            <g key={`d-${tg.id}`} aria-hidden='true'>
              <line
                x1={c.x} y1={c.y} x2={pin.x} y2={pin.y}
                className={inside ? 'stroke-ws-primary/40' : 'stroke-ws-muted/40'}
                strokeWidth={1}
                strokeDasharray='2 3'
              />
              <text
              pointerEvents='none'
                x={(c.x + pin.x) / 2}
                y={(c.y + pin.y) / 2 - 3}
                textAnchor='middle'
                className={`text-xs tabular-nums ${inside ? 'fill-ws-primary' : 'fill-ws-muted'}`}
              >
                {t('task.sensorSim.scaleBar', { meters: fmtLocale(Math.round(d), i18n.language) })}
              </text>
            </g>
          );
        })}

        {targets.map(tg => {
          const c = worldToScreen(tg.at, view);
          return (
            <g key={tg.id}>
              <circle
                cx={c.x} cy={c.y} r={tg.radiusMeters / view.metersPerPixel}
                className='fill-ws-primary/10 stroke-ws-primary/50'
                strokeDasharray='4 3'
              />
              <circle cx={c.x} cy={c.y} r={3} className='fill-ws-primary' />
              <text pointerEvents='none' x={c.x + 6} y={c.y - 6} className='fill-ws-muted text-xs'>{tg.name}</text>
            </g>
          );
        })}

        {showRing && (
          <>
            <circle cx={pin.x} cy={pin.y} r={RING_PX} className='fill-none stroke-ws-border' />
            {bands.map(b => (
              <path key={b.id} d={bandPath(b)} className='fill-none stroke-ws-primary/60' strokeWidth={5} />
            ))}
            <line x1={pin.x} y1={pin.y} x2={tip.x} y2={tip.y}
              className={`stroke-ws-text${disabled ? '' : ' cursor-grab active:cursor-grabbing'}`} strokeWidth={2} />
            {/* An arrowHEAD, not a dot. A circle on the end of a line says "handle"; a bearing needs
                to say which way it POINTS, and a dot at the tip is as consistent with pointing the
                other way. Rotated about the pin so the geometry stays in one place. */}
            <polygon
              points='0,-9 6,5 0,2 -6,5'
              transform={`translate(${tip.x} ${tip.y}) rotate(${heading})`}
              className={`fill-ws-text${disabled ? '' : ' cursor-grab active:cursor-grabbing'}`}
            />
            {/*
              The bearing, off the tip and in front of it.

              An arrow says which way; it does not say WHICH bearing, and a teacher checking a task
              against an authored heading needs the number. Placed beyond the tip along the same
              line so it reads as the arrow's own label rather than as a caption floating nearby,
              and clamped inside the canvas so a heading that points at a corner does not push it
              off the edge.
            */}
            <text
              pointerEvents='none'
              x={Math.min(Math.max(label.x, 6), Math.max(size.width - 6, 6))}
              y={Math.min(Math.max(label.y, 14), Math.max(size.height - 20, 14))}
              textAnchor={labelAnchor}
              className='fill-ws-text text-xs font-semibold'
            >
              {t('task.sensorSim.bearing', {
                cardinal: cardinalPointFromDegrees(heading),
                degrees: Math.round(heading),
              })}
            </text>
          </>
        )}

        {/* The grab cursor rides the pin itself, and on a hit circle the size of the hit TEST so
            the pointer changes exactly where a press would be caught -- `PIN_PX` is the drawn
            radius and says nothing about what is grabbable. Transparent, and after the ring so a
            press near the arrow still reaches the arrow's own tie-break above. */}
        {showPin && !disabled && (
          <circle cx={pin.x} cy={pin.y} r={PIN_GRAB_PX}
            className='fill-transparent cursor-grab active:cursor-grabbing' />
        )}
        <circle cx={pin.x} cy={pin.y} r={PIN_PX} className='fill-ws-surface stroke-ws-text' strokeWidth={2} />
        <circle cx={pin.x} cy={pin.y} r={3} className='fill-ws-text' />

        {/*
          Which way is up, stated rather than assumed.

          North IS up in this projection and always will be, but a blank field gives a reader no
          reason to believe that — and a bearing is meaningless without it. Fixed in the corner
          rather than drawn on the pin, because it describes the MAP, not the device.
        */}
        <g aria-hidden='true' transform={`translate(${size.width - 34} 34)`} className='fill-ws-muted'>
          <line x1={0} y1={-14} x2={0} y2={14} className='stroke-ws-border' />
          <line x1={-14} y1={0} x2={14} y2={0} className='stroke-ws-border' />
          <polygon points='0,-16 4,-9 -4,-9' className='fill-ws-text' />
          <text pointerEvents='none' x={0} y={-19} textAnchor='middle' className='fill-ws-text text-xs font-semibold'>{t('task.sensorSim.cardinalN')}</text>
          <text pointerEvents='none' x={25} y={4} textAnchor='middle' className='text-xs'>{t('task.sensorSim.cardinalE')}</text>
          <text pointerEvents='none' x={0} y={29} textAnchor='middle' className='text-xs'>{t('task.sensorSim.cardinalS')}</text>
          <text pointerEvents='none' x={-25} y={4} textAnchor='middle' className='text-xs'>{t('task.sensorSim.cardinalW')}</text>
        </g>

        {/* The scale bar is what makes a tile-free canvas readable: without it there is no way to
            tell a 10 m circle from a 10 km one. */}
        <g transform={`translate(12 ${size.height - 14})`}>
          <line x1={0} y1={0} x2={bar.pixels} y2={0} className='stroke-ws-muted' strokeWidth={2} />
          <line x1={0} y1={-4} x2={0} y2={4} className='stroke-ws-muted' strokeWidth={2} />
          <line x1={bar.pixels} y1={-4} x2={bar.pixels} y2={4} className='stroke-ws-muted' strokeWidth={2} />
          {/* One square, one label. The bar and the grid share a spacing precisely so this number
              answers both "how long is that" and "how big is a square". */}
          <text pointerEvents='none' x={bar.pixels + 6} y={4} className='fill-ws-muted text-xs'>
            {t('task.sensorSim.scaleBar', { meters: fmtLocale(bar.meters, i18n.language) })}
          </text>
        </g>
      </svg>

      {/*
        Reset, over the top-left of the map.

        The pin is easy to lose: drag it out past the tolerance a few times and the fit zooms out
        until the targets are a cluster in the middle. `clampToLimit` stops that becoming permanent,
        and this is the way back in one press. Top-LEFT because the rosette has the top-right and
        the scale bar the bottom-left, and it is a control rather than a legend, so it wants the
        corner a reader reaches first.
      */}
      {!disabled && (
        <IconButton
          size='sm'
          onClick={onReset}
          aria-label={t('task.sensorSim.resetDevice')}
          title={t('task.sensorSim.resetDevice')}
          className='absolute left-1.5 top-1.5 bg-ws-surface/90'
        >
          <RotateCcw aria-hidden='true' className='h-3.5 w-3.5' />
        </IconButton>
      )}

      {showPin && !disabled && (
        <div
          role='slider'
          tabIndex={0}
          aria-label={t('task.sensorSim.positionLabel')}
          aria-valuetext={distanceText}
          aria-valuemin={0}
          aria-valuemax={0}
          aria-valuenow={0}
          onKeyDown={e => {
            // A step in PIXELS rather than metres, so one press moves the same visible distance at
            // every zoom — which is what a keyboard user is actually aiming with.
            const step = view.metersPerPixel * 8;
            const move = (dNorth: number, dEast: number) => {
              e.preventDefault();
              onPositionChange(clampToLimit(screenToWorld(
                { x: pin.x + dEast * (step / view.metersPerPixel), y: pin.y - dNorth * (step / view.metersPerPixel) },
                view,
              ), targets));
            };
            if (e.key === 'ArrowUp') move(1, 0);
            else if (e.key === 'ArrowDown') move(-1, 0);
            else if (e.key === 'ArrowLeft') move(0, -1);
            else if (e.key === 'ArrowRight') move(0, 1);
          }}
          style={{ left: pin.x - 15, top: pin.y - 15 }}
          className={SLIDER_HANDLE_CLASS}
        />
      )}
      {showRing && !disabled && (
        <div
          role='slider'
          tabIndex={0}
          aria-label={t('task.sensorSim.headingLabel')}
          aria-valuemin={0}
          aria-valuemax={359}
          aria-valuenow={Math.round(heading)}
          aria-valuetext={headingText}
          onKeyDown={e => {
            const by = (d: number) => { e.preventDefault(); onHeadingChange((heading + d + 360) % 360); };
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') by(e.shiftKey ? 15 : 1);
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') by(e.shiftKey ? -15 : -1);
          }}
          style={{ left: tip.x - 15, top: tip.y - 15 }}
          className={SLIDER_HANDLE_CLASS}
        />
      )}

    </div>
  );
}
